import type {
  DataToSendEvent,
  ProjectType,
  SpaCustomParam,
  SpaEventId,
  SubMenuUploadFileType,
} from '@core/core/types'
import type { BaseObject, Nullable, ProjectElementInfo } from 'common/types'

import type { AppThunk } from '../store'
import { selectActiveProjectName, selectPreferredName } from '../store'
import { defaultSpaEventType, sendSpaEvent } from './spa'

export const getFolderSelectionParams = (
  fieldName: string,
  activeProjectName: string,
  databaseName = 'ГеоБД',
): SpaCustomParam[] => {
  return [
    { name: 'project_name', type: 'STRING', value: activeProjectName },
    { name: 'database_name', type: 'STRING', value: databaseName },
    { name: 'folder_name', type: 'STRING', value: fieldName },
  ]
}

export const getImportFileAddedParams = (
  wellName: string,
  fieldName: string,
  activeProjectName: string,
  databaseName = 'ГеоБД',
): SpaCustomParam[] => {
  return [
    { name: 'group_name', type: 'STRING', value: wellName },
    { name: 'project_name', type: 'STRING', value: activeProjectName },
    { name: 'folder_name', type: 'STRING', value: fieldName },
    { name: 'database_name', type: 'STRING', value: databaseName },
  ]
}

export const getProjectNameCustomParams = (activeProjectName?: string): SpaCustomParam[] => {
  return activeProjectName
    ? [{ name: 'project_name', type: 'STRING', value: activeProjectName }]
    : []
}

export const getImportGeobdSearchParams = (
  selectedGeoDBQuery: string[],
  activeProjectName?: string,
): SpaCustomParam[] => {
  const activeProjectNameParam = getProjectNameCustomParams(activeProjectName)
  const searchParams: SpaCustomParam[] = [
    { name: 'database_name', type: 'STRING', value: 'ГеоБД' },
    {
      name: 'database_filters',
      type: 'STRING',
      value: selectedGeoDBQuery.join(' '),
    },
  ]

  return [...activeProjectNameParam, ...searchParams]
}

export const getImportFileAddParam = (
  rowName: string,
  fieldName?: string,
  activeProjectName?: string,
): SpaCustomParam[] => {
  const activeProjectNameParam = getProjectNameCustomParams(activeProjectName)
  const folderParam: SpaCustomParam[] = fieldName
    ? [{ name: 'folder_name', type: 'STRING', value: fieldName }]
    : []

  const importFileParams: SpaCustomParam[] = [
    { name: 'database_name', type: 'STRING', value: 'ГеоБД' },
    { name: 'group_name', type: 'STRING', value: rowName },
  ]

  return [...activeProjectNameParam, ...folderParam, ...importFileParams]
}

export const getProjectDataAnalyticParams = (
  name: string | undefined,
  type: Nullable<ProjectType> | undefined,
): SpaCustomParam[] => {
  if (!type || !name) return []

  return [
    { name: 'project_name', type: 'STRING', value: name },
    { name: 'project_type', type: 'STRING', value: type },
  ]
}

export const getDataBaseCustomParams = (
  count: number,
  activeProjectName?: string,
): SpaCustomParam[] => {
  if (!activeProjectName) return []

  return [
    { name: 'project_name', type: 'STRING', value: activeProjectName },
    { name: 'count', type: 'STRING', value: count },
  ]
}

export const getImportDataAnalyticParams = (
  name: string | undefined,
  type: SubMenuUploadFileType | BaseObject | undefined,
): SpaCustomParam[] => {
  if (!name) return []

  if (type) {
    return [
      { name: 'project_name', type: 'STRING', value: name },
      { name: 'object_name', type: 'STRING', value: type },
    ]
  }

  return [{ name: 'project_name', type: 'STRING', value: name }]
}

const subMenuUploadFileMapper: Partial<Record<SubMenuUploadFileType, SpaEventId>> = {
  Survey2D: 'import from file_seismic',
  Horizon: 'import from file_seismic',
  Fault: 'import from file_seismic',
  Polygons: 'import from file_polygon',
  Maps: 'import from file_maps',
  VSP: 'import from file_well',
}

export const getDataToSendEvent = (
  activeProjectName: string | undefined,
  type: SubMenuUploadFileType | undefined,
): DataToSendEvent | undefined => {
  if (!type) return

  const id = subMenuUploadFileMapper[type]

  switch (id) {
    case 'import from file_seismic':
      return {
        id,
        params: getImportDataAnalyticParams(activeProjectName, type),
      }
    case 'import from file_maps':
    case 'import from file_well':
    case 'import from file_polygon':
      return {
        id,
        params: getProjectNameCustomParams(activeProjectName),
      }
    default:
      return undefined
  }
}

export const getUploadDataAnalyticParams = (
  name: string | undefined,
  type: SubMenuUploadFileType | undefined,
): SpaCustomParam[] => {
  if (!name || !type) return []

  return [
    { name: 'project_name', type: 'STRING', value: name },
    { name: 'object_type', type: 'STRING', value: type },
  ]
}

export const sendSpaForProjectElementAction =
  (spaId: SpaEventId, element?: ProjectElementInfo): AppThunk =>
  (dispatch, getState) => {
    if (!element) return

    const state = getState()
    const login = selectPreferredName(state)
    const projectName = selectActiveProjectName(state)
    const customParams = getImportDataAnalyticParams(projectName, element.elementType)

    if (login) {
      const userIdParam = { as_user_id: true, value: login }

      sendSpaEvent(defaultSpaEventType, {
        componentid: spaId,
        constaeventtype: 'click',
        component: 'OTHER',
        customparams: [...customParams, userIdParam],
      })
    }
  }
